import React, { useEffect, useState } from "react";
import { selectSearchFlightsForm } from "./searchFlightsSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { format, isBefore, startOfDay, parseISO } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTicketAlt } from "@fortawesome/free-solid-svg-icons/faTicketAlt";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons/faExternalLinkAlt";
import { useTranslation } from "react-i18next";
import { ConfigSliceState, selectConfig } from "../config/ConfigSlice";
import { Mapper, SearchFlightsForm } from "../../types";
import { trackFlightBookingLink } from "../analytics/AnalyticsActions";
import {
  FlightResultContent,
  selectChosenFlightsResults,
} from "./searchFlightsSlice";

function BookingButton() {
  const { t, i18n } = useTranslation();
  const chosenResults: FlightResultContent = useAppSelector(
    selectChosenFlightsResults
  );
  const {
    originAirport,
    destinationAirport,
    departingDate,
    returningDate,
    roundTrip,
  }: SearchFlightsForm = useAppSelector(selectSearchFlightsForm);
  const { mapper }: { mapper: Mapper } = useAppSelector(selectConfig);
  const [url, setUrl] = useState<string>();
  const dispatch = useAppDispatch();
  const languageCode = i18n?.language?.split("-")[0];
  const { components }: ConfigSliceState = useAppSelector(selectConfig);
  const isRoundTripDisplay = components.Inputs.RoundTrip.display;

  useEffect(() => {
    let generatedUrl;
    let selectedDateFormat = mapper.bookingLink.formats.dateFormat;

    // Check if this config has transition logic (ANA-dom)
    if (mapper.bookingLink.urlNew && mapper.bookingLink.transitionDate) {
      const transitionDate =
        mapper.bookingLink.transitionDateOverride ||
        mapper.bookingLink.transitionDate;
      const departingDateOnly = startOfDay(departingDate);
      const transitionDateOnly = startOfDay(parseISO(transitionDate));

      // If departure date is on or after transition date, use new URL
      if (!isBefore(departingDateOnly, transitionDateOnly)) {
        generatedUrl = mapper.bookingLink.urlNew;
        selectedDateFormat =
          mapper.bookingLink.newSiteDateFormat || selectedDateFormat;
      } else {
        // Use old URL for dates before transition
        generatedUrl = mapper.bookingLink.url;
      }
    } else if (
      mapper.bookingLink.urlDomestic &&
      mapper.bookingLink.urlIntl &&
      mapper.bookingLink.domesticCountryCode
    ) {
      const depCountryCode = originAirport.country.code;
      const arrCountryCode = destinationAirport.country.code;
      if (!depCountryCode || !arrCountryCode) {
        generatedUrl = mapper.bookingLink.url;
      } else if (depCountryCode !== arrCountryCode) {
        generatedUrl = mapper.bookingLink.urlIntl;
      } else if (depCountryCode === mapper.bookingLink.domesticCountryCode) {
        generatedUrl = mapper.bookingLink.urlDomestic;
      }
    } else {
      generatedUrl = mapper.bookingLink.url;
    }
    generatedUrl = generateUrlWithParams(
      generatedUrl,
      originAirport.port.code,
      destinationAirport.port.code,
      departingDate,
      returningDate,
      selectedDateFormat,
      languageCode,
      isRoundTripDisplay,
      roundTrip
    );
    setUrl(generatedUrl);
  }, [
    originAirport,
    destinationAirport,
    departingDate,
    returningDate,
    mapper.bookingLink,
    languageCode,
    roundTrip,
  ]);
  const onClickBookingBtn = () => {
    const data = {};
    data["BookingLink"] = url;
    data["DepartureAirportCode"] = originAirport.port.code;
    data["ArrivalAirportCode"] = destinationAirport.port.code;
    data["SchedDepartureLocalDate"] = format(departingDate, "yyyy-MM-dd");
    if (isRoundTripDisplay) {
      data["SchedReturnLocalDate"] = roundTrip
        ? format(returningDate, "yyyy-MM-dd")
        : null;
      data["FlightType"] = roundTrip ? "return" : "single";
    } else {
      data["SchedReturnLocalDate"] = format(returningDate, "yyyy-MM-dd");
      data["FlightType"] = "return";
    }
    dispatch(trackFlightBookingLink(data));
  };
  return (
    chosenResults.filtered &&
    chosenResults.filtered.length > 0 && (
      <a
        href={url}
        id="booking-button"
        className="button is-link"
        target={mapper.bookingLink.target}
        onClick={onClickBookingBtn}
      >
        <span className="icon">
          <FontAwesomeIcon icon={faTicketAlt} />
        </span>
        <span>{t("BOOK_FLIGHT")}</span>
        <span className="icon is-small booking-external-icon" aria-hidden>
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </span>
      </a>
    )
  );
}

function generateUrlWithParams(
  url,
  depCode,
  arrCode,
  departingDate,
  returningDate,
  dateFormat,
  languageCode,
  isRoundTripDisplay,
  roundTrip
) {
  const schedReturnLocalDate = !isRoundTripDisplay
    ? format(returningDate, dateFormat)
    : roundTrip
    ? format(returningDate, dateFormat)
    : null;

  // Determine trip type for new booking site
  const tripType = roundTrip ? "roundtrip" : "onewayOrMulticity";
  // Determine boolean value for old booking site roundFlag
  const itinSearchModeBool = roundTrip ? "1" : "0";

  // Conditional parameter names based on trip type
  let originParam, destinationParam, departureDateParam, returnDateParam;

  if (roundTrip) {
    // Round-trip: standard parameters
    originParam = "origin";
    destinationParam = "destination";
    departureDateParam = "departureDate";
    returnDateParam = `&returnDate=${format(returningDate, dateFormat)}`;
  } else {
    // One-way: numbered parameters, no returnDate
    originParam = "origin1";
    destinationParam = "destination1";
    departureDateParam = "departureDate1";
    returnDateParam = ""; // Remove returnDate for one-way
  }

  return url
    .replace("%OriginParam%", originParam)
    .replace("%DestinationParam%", destinationParam)
    .replace("%DepartureDateParam%", departureDateParam)
    .replace("%DepartureAirportCode%", depCode)
    .replace("%ArrivalAirportCode%", arrCode)
    .replace("%SchedDepartureLocalDate%", format(departingDate, dateFormat))
    .replace("%SchedReturnLocalDate%", schedReturnLocalDate)
    .replace("%ReturnDateParam%", returnDateParam)
    .replace("%LanguageCode%", languageCode)
    .replace(
      "%FlightType%",
      !isRoundTripDisplay ? "return" : roundTrip ? "return" : "single"
    )
    .replace("%TripType%", tripType)
    .replace("%ItinSearchModeBool%", itinSearchModeBool);
}

export default BookingButton;
